import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { fetchAllRatings } from "../surveyReportApi"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useMemo, useState } from "react"
import { DateTime } from "luxon"
import { AllRatingsReport, Survey, TableRatingsReport } from "../surveyTypes"
import { DropdownExportButtonRatings } from "./DropdownExportButtonRatings"
import { makeStyles } from "tss-react/mui"
import { displaySmiley } from "./SmileySummaryCard"
import { useSurveys } from "../hooks/useSurveys"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useDateTime } from "../../../../localization/useDateTime"

export function transformData(data: AllRatingsReport): TableRatingsReport[] {
    const startDate = data.reportDetails.startDate
    const endDate = data.reportDetails.endDate

    const flattened: TableRatingsReport[] = data.ratings.flatMap((rating) => {
        return rating.survey.questions.map((question) => ({
            startDate,
            endDate,
            ...rating,
            question,
        }))
    })

    return flattened
}

const useStyles = makeStyles()((_theme) => ({
    textWrap: {
        inlineSize: "8rem",
        overflowWrap: "break-word",
    },
}))

export function AllRatingsTab() {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()

    const [survey, setSurvey] = useState<Survey | null>(null)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateRange, setDateRange] = useState(() => ({
        startDate: startDate ?? today.minus({ days: 14 }),
        endDate: endDate ?? today,
    }))

    const { l10n } = useAppLocalization()
    const { SelectSurvey } = useSurveys()

    const ratingsResult = useApiQuery(fetchAllRatings, {
        queryName: "survey-all-ratings",
        dependencies: {
            surveyId: survey?.id ?? 0,
            startDate: dateRange.startDate.toISODate(),
            endDate: dateRange.endDate.toISODate(),
        },
        select: transformData,
        enabled: Boolean(survey?.id),
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable<TableRatingsReport>(ratingsResult)

    const columns = useMemo<MRT_ColumnDef<TableRatingsReport>[]>(
        () => [
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("all-rating-time"),
                    id: "created",
                },
                (row) => row.created,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("all-rating-question"),
                accessorKey: "question.title",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("all-rating-topics"),
                accessorFn(originalRow) {
                    return originalRow.question.smiley.topics.map((topic) => topic.value).join(", ") ?? ""
                },
                Cell: ({ row }) => {
                    return (
                        <>
                            <p className={classes.textWrap}>
                                {row.original.question.smiley.topics.map((topic) => topic.value).join(", ") ?? ""}
                            </p>
                        </>
                    )
                },
            }),
            columnFactory.typeColumn({
                header: l10n.getString("all-rating-score"),
                accessorFn: (originalRow) => originalRow.question.smiley.value,
                Cell: ({ row }) => {
                    const smiley = row.original.question.smiley.value
                    const src = displaySmiley(smiley)

                    return (
                        <>
                            <img src={src} height={50} />
                        </>
                    )
                },
            }),
        ],
        [],
    )

    return (
        <ApiQueryTable
            columns={columns}
            enableRowSelection
            enableClickToCopy={false}
            initialState={{ density: "spacious" }}
            renderToolbarInternalActions={({ table }) => (
                <StandardTableFilter variant="right" table={table}>
                    <DropdownExportButtonRatings
                        rowsIsSelected={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        containsRows={table.getPrePaginationRowModel().rows.length === 0}
                        allRowsWithFilter={table.getPrePaginationRowModel().rows}
                        selectedRows={table.getSelectedRowModel().rows}
                        chosenStartDate={dateRange.startDate?.toISODate() ?? ""}
                        chosenEndDate={dateRange.endDate?.toISODate() ?? ""}
                    />
                </StandardTableFilter>
            )}
            renderTopToolbarCustomActions={() => (
                <StandardTableFilter variant="left">
                    <SelectSurvey variant="outlined" size="medium" survey={survey} setSurvey={setSurvey} />
                    <StandardDateRangeFilter value={dateRange} onChange={setDateRange} />
                </StandardTableFilter>
            )}
        />
    )
}
